import {
  Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { format } from "date-fns";
import { ChevronRight } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  AgendaWidgetContainerStyle,
  Appointment,
  AppointmentList,
  DayTitle, TypographyAppointmentHours,
  TypographyAppointmentInfo,
  TypographyAppointmentTitle
} from "./AgendaWidget.styles";
import { courseColor, expiringLabel, goToDetail, handleType } from "./utils";
import { ButtonDigited } from "../../components/Button/Button.variants";
import { CONTENT_TYPE } from "../../consts";
import { useContentQuery } from "../../queries";
import { useNavigate } from "../../services";
import { useLanguageStore } from "../../stores";
import type {
  CalendarLearningObject,
  DayToHighlight
} from "../../types";
import { CommonWidgetContainer } from "../Commons/CommonWidgetContainer.styles";


export const AgendaWidgetContainer = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const language = useLanguageStore(state => state.language)?.id;
  //const language = useLanguageStore(state => state.language)?.id;
  //const locale = useDateFnsLocale({ localeCode: language || "it" }); //it come default, ma appena arriva la response dal servizio languages, si aggiorna con il primary_language

  // const currentDay = new Date();

  // const { data: pageStructureWidgetAgenda } = useStructuresQuery({
  //   pageName: PAGE_NAME.WIDGET_AGENDA
  // });
  // const agendaContentsPath = pageStructureWidgetAgenda?.relativePaths?.find(
  //   (relativePath) => (relativePath.serviceType === WIDGET.CALENDAR)
  // );

  //const widgetSelected = format(new Date(), "yyyy/MM/dd");
  const widgetStartDate =
  format(new Date(`${format(new Date(), "yyyy")}/${format(new Date(), "MM")}/${format(new Date(), "dd")}`), "yyyy/MM/dd");

  // gestione data di tre mesi dopo la data odierna
  const threeMonthsFromNow = (() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 3);
    return d.toISOString().split("T")[0];
  })();

  const {
    data: learnerAgendaDaysWidget
  } = useContentQuery<{ daysToHighlight: DayToHighlight[] }>({
    enabled,
    path: `/learning-catalogue/{corporateId}/{initiativeId}/learnerElementsAgenda?startDate=${widgetStartDate?.replaceAll("/", "-")}&endDate=${threeMonthsFromNow}&pageNumber=${0}&pageSize=${8}`
  });

  function arrayDateToTimestamp(year: number, month:number, day:number) {
    return new Date(year, month - 1, day).getTime();
  }

  //@ts-ignore
  function handleApointments(day) {
    let newObj = [];
    let completeDate = null;

    // check se sono presenti smartslots, per unirli ai learningObjects
    if (day.areThereSmartSlots && day?.smartLearningSlots?.length > 0) {
      let dayCheck = day.day;
      const daySmartSlot = arrayDateToTimestamp(dayCheck[0], dayCheck[1], dayCheck[2]);

      newObj = day.learningObjects
        ? [...day.learningObjects, ...day.smartLearningSlots]
        : [...day.smartLearningSlots];

      return {
        ...day,
        appointments: newObj,
        completeDate: daySmartSlot
      };
    }
    if(day.learningObjects?.length > 0) {

      newObj = day.learningObjects && [...day.learningObjects];
      completeDate = day.learningObjects[0].startDateTime;
    }
    return {
      ...day,
      appointments: newObj,
      completeDate: completeDate
    };
  }

  const appointments = useMemo<DayToHighlight[]>(() => {
    if(!learnerAgendaDaysWidget?.daysToHighlight) return [];

    return learnerAgendaDaysWidget?.daysToHighlight
      .map(handleApointments)
      .filter(
        (day): day is DayToHighlight =>
          day.appointments?.length > 0
      );
  }, [learnerAgendaDaysWidget]);

  function getAppointmentTime(lo: CalendarLearningObject) {
    const startDateTime = format(new Date(lo.startDateTime), "HH:mm");
    const endDateTime = format(new Date(lo.endDateTime), "HH:mm");
    return `${startDateTime} - ${endDateTime}`;
  }

  function normalizeTime(time:string) {
    const [hours, minutes] = time.split(":").map(s => s.trim());
    return `${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}`;
  }

  function handleTimeString(lo:CalendarLearningObject) {
    if (lo.objectType === CONTENT_TYPE.SMART_LEARNING_SLOT) {
      return `${normalizeTime(lo.startTime)} - ${normalizeTime(lo.endTime)}`;
    }
    return getAppointmentTime(lo);
  }

  return (
    <CommonWidgetContainer id="agenda-widget" hasSidePadding={ false } isLoading={ false }>
      { (appointments.length === 0) ? (
        <Typography color={ theme.customColors.exploreCardText } fontSize="0.85rem" fontWeight="400" paddingLeft="1rem">
          { t("no_appointments_in_agenda") }
        </Typography>
      ) : (
        appointments?.map((day: DayToHighlight, index:number) => {
          /*

          if(
            day.completeDate && isBeforeTodayAt0001(day.completeDate)
          ) return <></>; */

          //const fixedDate = day.day;

          //const [year, month, dayInt] = fixedDate;
          /* const appointmentDate = format(
            new Date(year, month - 1, dayInt),
            "d MMMM y",
            { locale }
          );*/

          // let isToday: boolean = isTodayAssertion(day.completeDate ?? 0);

          function formatItalianDate(date: Date): string {
            const options: Intl.DateTimeFormatOptions = {
              day: "numeric",
              month: "long",
              year: "numeric"
            };

            const formatted = new Intl.DateTimeFormat("it-IT", options).format(date);
            // Capitalize the first letter of the month
            const capitalized = formatted.replace(/(\d+)\s+(\w+)/, (match, day, month) => {
              return `${day} ${month.charAt(0).toUpperCase()}${month.slice(1)}`;
            });

            return `${t("today")} | ${capitalized}`;
          }

          return (
            <AgendaWidgetContainerStyle key={ `day-${day.completeDate}-agenda--${language}` }>
              <DayTitle role="presentation" tabIndex={ 0 } aria-labelledby={ "appointment-date-" + index + "-" + day.completeDate }>
                <Typography
                  fontSize="0.875rem"
                  lineHeight="1.25rem"
                  fontWeight={ 600 }
                  id={ "appointment-date-" + index + "-" + day.completeDate }>
                  { formatItalianDate(new Date()) }
                </Typography>
              </DayTitle>
              { /* lista appuntamenti */ }
              <AppointmentList role="list">
                { day.appointments?.map((lo: CalendarLearningObject, appointmentInd:number) => {
                  return (
                    <Appointment
                      key={ appointmentInd }
                      // isDigital={ lo.typeDigital }
                      color={ courseColor(lo, theme) }
                      tabIndex={ 0 }
                      aria-labelledby={ "agenda-appointment-type-" + lo.id + " " + "agenda-label-" + lo.id }

                      onClick={ () => goToDetail(lo, navigate, lo.dateObjectType ?? undefined) }
                    >
                      <div style={ { width: "100%" } }>
                        <TypographyAppointmentTitle
                          // isDigital={ lo.typeDigital }
                          id={ "agenda-appointment-type-" + lo.id }
                          color={ courseColor(lo, theme) }
                        >
                          { /* @ts-ignore */ }
                          { expiringLabel(lo.learningObjectTypology || lo.objectType) }
                          { /* @ts-ignore */ }
                          { handleType(lo.learningObjectType || lo.objectType, lo.learningObjectTypology).toUpperCase() }
                        </TypographyAppointmentTitle>
                        <div style={ {
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%"
                        } }>
                          <TypographyAppointmentInfo
                            style={ { flexGrow: 1 } }
                            id={ "agenda-label-" + lo.id }
                            aria-label={ lo.title }
                          >
                            { lo.title ?? t("learning_time") }
                          </TypographyAppointmentInfo>
                          {
                            ((lo.startDateTime && lo.endDateTime) || (lo.startTime && lo.endTime)) && (
                              <TypographyAppointmentHours
                                style={ { minWidth: "4.8rem", marginLeft: "3rem" } }
                                aria-label={  handleTimeString(lo) }
                              >
                                { handleTimeString(lo) }
                              </TypographyAppointmentHours>

                            )
                          }
                        </div>
                      </div>
                    </Appointment>
                  );
                }) }
              </AppointmentList>

            </AgendaWidgetContainerStyle>
          );
        })
      ) }

      <ButtonDigited href="/agenda" typology="secondary" buttonSize="sm"  selfPosition="flex-end" sx={ { marginRight:"1rem" } }>
        <>
          { t("go_to_agenda") + " " } <ChevronRight size="0.875rem" />
        </>
      </ButtonDigited>
    </CommonWidgetContainer>
  );
};

